import {Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, output, input, effect} from '@angular/core';
import {DataService} from "../../../../services/data.service";
import {ProjectStatus, ViewMode, Right, FileType} from "../../../interfaces";
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Router} from "@angular/router";
import {Subscription} from "rxjs";
import {ProjectService} from "../../../../services/project.service";
import {AccessService} from "../../../../services/access.service";
import Swal from "sweetalert2";
import {ToastrService} from "ngx-toastr";
import {NgIf, NgFor, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {ProjectStatusPipe} from '../../../../pipes/project-status.pipe';
import {FiletypePipe} from '../../../../pipes/filetype.pipe';

@Component({
    selector: 'app-project-idea',
    templateUrl: './project-idea.component.html',
    styleUrl: './project-idea.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, DatePipe, ProjectStatusPipe, FiletypePipe]
})
export class ProjectIdeaComponent implements OnInit, OnDestroy {

    projectService = inject(ProjectService);
    accessService = inject(AccessService);
    private dataService = inject(DataService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private toastrService = inject(ToastrService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    @ViewChild('fileInput') fileInput: ElementRef;

    readonly ViewMode = ViewMode;
    readonly ProjectStatus = ProjectStatus;
    readonly Right = Right;

    projectStatusList = Object.values(ProjectStatus);

    viewMode!: ViewMode;
    countries: any[];
    project: any;
    form!: UntypedFormGroup;
    files: any[] = [];
    locations: any[] = [];

    private countryChanges$: Subscription;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getProject();
        }
    })

    ngOnInit(): void {
        this.viewMode = this.PID() == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            countries: [null],
            id_location: [''],
            ideator: [''],
            title: ['', Validators.required],
            situation: ['', Validators.required],
            benefit: ['', Validators.required],
            status: [ProjectStatus.CREATED, Validators.required],
            category: [''],
            quickwin_responsible: [null],
            quickwin_comment: [null],

            countryNames: [''],
            location: [''],
        });

        this.countryChanges$ = this.form.get('countries').valueChanges.subscribe(val => {
            this.form.get('id_location').patchValue('');

            // if only 1 country is selected load locations
            if (val.length === 1) {
                this.locations = val[0].locations;
            }
        });

        if (this.PID() !== '0') {
            this.getProject();
        }

        this.dataService.request("Country/listWithLocations").subscribe(response => {
            this.countries = response.countries;

            // move first entry (-Gruppe-) to the end
            this.countries.push(this.countries.shift());

            this.setCountries();
        })
    }

    ngOnDestroy(): void {
        this.countryChanges$?.unsubscribe();
    }

    triggerFileInput() {
        this.fileInput.nativeElement.click();
    }

    getFileData(file: any): void {
        if (file.id == null) {
            this.openFile(file.data, file.name);
        } else {
            this.dataService.request("File/getData", {
                id: file.id
            }).subscribe(response => {
                if (response.data.data != null) {
                    this.openFile('data:' + response.data.type + ';base64,' + response.data.data, response.data.name);
                }
            });
        }
    }

    openFile(data: string, name: string): void {
        const link = document.createElement('a');

        link.setAttribute('href', data);
        link.setAttribute('download', name);
        link.click();
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    setCountries(): void {
        if (!this.countries || !this.project) {
            return;
        }

        // set country
        this.form.patchValue({
            countries: this.countries.filter(country => {
                return this.project.countries.map(country => country.id_country).includes(country.id);
            }),
        });
    }

    getProject(): void {
        this.dataService.request('Project/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            this.patchForm();
            this.setCountries();
        });

        this.dataService.request('File/listAll', {
            PID: this.PID(),
            RID: null,
            category: FileType.IDEA,
        }).subscribe(response => {
            this.files = response.files;
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.project.id,
            //countries: this.project.countries, // this is done in setCountries()
            id_location: this.project.id_location ?? '',
            ideator: this.project.ideator,
            status: this.project.status,
            title: this.project.title,
            situation: this.project.situation,
            benefit: this.project.benefit,
            category: this.project.category,
            quickwin_responsible: this.project.quickwin_responsible,
            quickwin_comment: this.project.quickwin_comment,

            countryNames: this.project.countries.map(country => country.name).join(', '), // for display only
            location: this.project.location,
        });

        if (this.form.value.category === 'quickwin') {
            this.form.get('quickwin_responsible').addValidators(Validators.required);
            this.form.get('quickwin_responsible').updateValueAndValidity();
        }
    }

    save(): void {
        this.dataService.request('Project/save', {
            project: this.form.getRawValue(),
        }).subscribe(response => {
            if (response.errors.length == 0) {
                this.dataService.request('File/save', {
                    PID: response.insert.lastID,
                    RID: null,
                    files: this.files,
                }).subscribe(() => {
                    this.toastrService.success('Erfolgreich gespeichert')
                    this.router.navigate(['/']);
                });
            }
        });
    }

    update(): void {
        this.dataService.request('Project/updateIdea', {
            project: this.form.getRawValue(),
        }).subscribe(() => {
            this.dataService.request('File/save', {
                PID: this.PID(),
                RID: null,
                files: this.files,
            }).subscribe(() => {
                this.toastrService.success('Erfolgreich gespeichert')
                this.setMode(ViewMode.VIEW);
                this.getProject();
                this.changeProject.emit();
            });
        });
    }

    cancelCreate(): void {
        this.router.navigate(['/']);
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    handleInputChange(e): void {
        for (let i = 0; i < e.target.files.length; i++) {
            const reader = new FileReader();
            reader.onload = (progressEvent) => {
                this.files.push({
                    name: e.target.files[i].name,
                    size: e.target.files[i].size,
                    type: e.target.files[i].type,
                    data: progressEvent.target.result,
                    id: null,
                    category: FileType.IDEA,
                });
                if (i == e.target.files.length - 1) {
                    this.fileInput.nativeElement.value = '';
                }
            };
            reader.readAsDataURL(e.target.files[i]);
        }
    }

    deleteOrRemoveFile(file: any) {
        if (file.id == null) {
            this.files.splice(this.files.indexOf(file), 1);
        } else {
            this.deleteFile(file);
        }
    }

    deleteFile(file: any): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Anhang wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('File/delete', {
                    id: file.id
                }).subscribe(() => {
                    this.getProject();
                });
            }
        });
    }

    // only display the location if there is only one country selected, and it is not the group
    displayLocation(): boolean {
        if (!this.form.value.countries) return false;
        if (this.form.value.countries.length != 1) return false;

        return this.form.value.countries[0].name !== '-Gruppe-'
    }
}
